import { useContext, useEffect, useState } from "react";
import { MantineTheme, useMantineTheme, useMantineColorScheme } from "@mantine/core";

import { Container, Grid2 as Grid, Grid2Props as GridProps } from "@mui/material";

import {
  ActionIcon,
  Button,
  CopyButton,
  Divider,
  Modal,
  Select,
  SelectProps,
  Table,
  Text,
  TextProps,
  Tooltip,
  Badge,
  Paper,
  Card,
  Group,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconReload, IconX, IconClock, IconBook } from "@tabler/icons-react";

import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Colors, YesOrNoList } from "../../Lib/data";
import { CourseProps, PaginationProps, PlanProps } from "../../Lib/types";

import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import { generateRandomString, getEllipsisFromString } from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import { Link, useNavigate } from "react-router-dom";
import "./styles.scss";

const selectProps: SelectProps = {
  allowDeselect: false,
};
const gridItemProps: GridProps = {
  size: {
    xl: 3,
    lg: 4,
    md: 6,
    sm: 6,
    xs: 12,
  },
};
const Courses: React.FC = () => {
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });

  const filterForm = useForm({
    initialValues: {
      isActive: "unspecified",
    },
    validate: {
      isActive: (value) => (value && value.length > 0 ? null : "Please select a role"),
    },
  });

  const {
    data: courses,
    reloadData: reloadCourses,
    isLoading: isLoadingCourses,
  } = usePerformRequest<PaginatedResponse<CourseProps[]>>({
    route: Endpoints.GetPurchasedCourses().route,
    method: Endpoints.GetPurchasedCourses().method,
  });
  useEffect(() => {
    reloadCourses();
  }, [filterForm.values.isActive]);

  const [courseImageFiles, setCourseImageFiles] = useState<File[]>([]);
  const courseForm = useForm<CourseProps>({
    initialValues: {
      id: "",
      name: "",
      description: "",
      thumbnail: "",
      images: [],
      planID: "",
      teacherHistory: [],
      isActive: false,
      createdAt: 0,
      createdBy: "",
      teacherID: "",
    },
  });

  const [viewingCourse, setViewingCourse] = useState<CourseProps>();

  const [isCourseModalOpened, { open: OpenCourseModal, close: CloseCourseModal }] =
    useDisclosure(false);

  const [isCreatingOrUpdatingCourse, setCreatingOrUpdatingCourse] = useState<boolean>(false);

  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };
  const showImageCarousel = courseImageFiles.length > 0 || courseForm.values.images.length > 0;
  const navigate = useNavigate();
  return (
    <Container maxWidth="lg">
      <div className="courses-container">
        <br />
        <div className="component-header flex-row align-end justify-between">
          <div className="header-content">
            <Text
              className="label"
              fw={700}
              fz={28}
              style={{
                background: "linear-gradient(45deg, #228be6, #4dabf7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("courses.title")}
            </Text>
            <Text c="dimmed" fz="sm" mt={4}>
              {t("courses.courseDetails")}
            </Text>
          </div>
          <div className="flex-row align-center">
            <Button
              color={Colors.PurplePrimary}
              variant="light"
              onClick={reloadCourses}
              loading={isLoadingCourses}
              rightSection={<IconReload size={16} />}
              radius="xl"
              size="md"
            >
              {t("common.refresh")}
            </Button>
          </div>
        </div>
        <Divider w="100%" my={20} color="gray.2" style={{ opacity: 0.5 }} />

        <div className="courses">
          {isLoadingCourses ? (
            <MegaLoader
              size={50}
              containerStyle={{
                marginTop: 45,
                marginBottom: 45,
              }}
            />
          ) : (
            <>
              {!courses?.data || courses?.data.length === 0 ? (
                <Paper p={40} radius="md" withBorder className="empty-state">
                  <div className="empty-state-content">
                    <IconBook size={48} color={Colors.BluePrimary} style={{ opacity: 0.5 }} />
                    <Text fw={600} fz={20} mt={16} c={Colors.BluePrimary}>
                      {t("courses.noCourses")}
                    </Text>
                    <Link to="/plans">
                      <Button
                        variant="outline"
                        color={Colors.PurplePrimary}
                        fz="sm"
                        c="dimmed"
                        ta="center"
                        mt={8}
                      >
                        {t("courses.viewPlans")}
                      </Button>
                    </Link>
                  </div>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {courses?.data
                    .filter((t) => t.id !== context?.profile?.id)
                    .map((course) => (
                      <Grid {...gridItemProps} key={course.id}>
                        <Card
                          shadow="sm"
                          padding="lg"
                          radius="md"
                          withBorder
                          className="course-card"
                        >
                          <div
                            className="course-card-background"
                            style={{
                              backgroundImage:
                                course.thumbnail && course.thumbnail !== ""
                                  ? `url(${course.thumbnail})`
                                  : course.images && course.images.length > 0
                                  ? `url(${course.images[0]})`
                                  : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 0,
                            }}
                          />
                          <div
                            className="course-card-content"
                            style={{ position: "relative", zIndex: 1 }}
                          >
                            <Group justify="space-between" mb="xs">
                              <Text fw={500} lineClamp={2} c="white">
                                {course.name}
                              </Text>
                            </Group>

                            <Text size="sm" c="white" lineClamp={6} mb="md">
                              {course.description}
                            </Text>

                            <Button
                              variant="filled"
                              color={Colors.PurplePrimary}
                              fullWidth
                              leftSection={<IconEye size={16} />}
                              onClick={() => {
                                OpenCourseModal();
                                setViewingCourse(course);
                              }}
                              radius="md"
                              size="sm"
                            >
                              {t("courses.viewCourse")}
                            </Button>
                          </div>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        opened={isCourseModalOpened}
        onClose={CloseCourseModal}
        centered
        size="xl"
        withCloseButton={false}
        styles={{
          content: {
            background:
              colorScheme === "dark"
                ? "var(--mantine-color-dark-7)"
                : "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
            borderRadius: "16px",
            boxShadow:
              colorScheme === "dark"
                ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                : "0 8px 32px rgba(0, 0, 0, 0.1)",
          },
          header: {
            margin: 0,
            padding: "24px 24px 0",
          },
          body: {
            padding: "24px",
          },
        }}
        transitionProps={{
          transition: "fade",
          duration: 200,
        }}
      >
        <div className="course-modal flex-col">
          <div className="flex-row align-center width-100 justify-between">
            <div className="flex-row align-center">
              <Text
                tt="capitalize"
                c={Colors.BluePrimary}
                fw={600}
                fz={20}
                style={{
                  background: "linear-gradient(45deg, #228be6, #4dabf7)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("courses.courseDetails")}
              </Text>
            </div>
            <ActionIcon
              variant="light"
              color={Colors.BluePrimary}
              onClick={CloseCourseModal}
              size="lg"
              radius="xl"
              styles={(theme) => ({
                root: {
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "rotate(90deg)",
                    backgroundColor: theme.colors.blue[0],
                  },
                },
              })}
            >
              <IconX size={18} />
            </ActionIcon>
          </div>

          {viewingCourse && (
            <>
              <div className="course-details-container">
                <div className="course-content-grid">
                  <div
                    className="course-thumbnail"
                    style={{
                      backgroundImage:
                        viewingCourse.thumbnail && viewingCourse.thumbnail !== ""
                          ? `url(${viewingCourse.thumbnail})`
                          : viewingCourse.images && viewingCourse.images.length > 0
                          ? `url(${viewingCourse.images[0]})`
                          : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      width: "100%",
                      height: "300px",
                      borderRadius: "8px",
                    }}
                  />

                  <div className="course-info">
                    <div className="course-header">
                      <div className="course-title">
                        <Text fw={700} fz={24} c={Colors.BluePrimary}>
                          {viewingCourse.name}
                        </Text>
                        <Badge
                          color={viewingCourse.isActive ? "green" : "red"}
                          variant="light"
                          size="lg"
                          h={34}
                          w={130}
                          radius="xl"
                        >
                          {viewingCourse.isActive ? t("common.active") : t("common.inactive")}
                        </Badge>
                      </div>
                      <Text c="dimmed" fz="sm" mt={4}>
                        {t("common.createdAt")}{" "}
                        {dayjs(viewingCourse.createdAt).format("MMMM DD, YYYY")}
                      </Text>
                    </div>

                    <Paper withBorder p="xl" radius="md" mt={24} className="description-card">
                      <Text fw={600} mb={12} c={Colors.BluePrimary}>
                        {t("common.description")}
                      </Text>
                      <Text>{viewingCourse.description}</Text>
                    </Paper>

                    <div className="course-actions" style={{ marginTop: 32 }}>
                      <Link to={`/d/course/${viewingCourse.id}`}>
                        <Button
                          color={Colors.PurplePrimary}
                          c={Colors.White}
                          style={{ color: `${Colors.White} !important` }}
                          size="md"
                          radius="xl"
                          styles={(theme) => ({
                            root: {
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: theme.shadows.md,
                              },
                            },
                          })}
                        >
                          <Text c={Colors.White}>{t("courses.viewCourse")}</Text>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default Courses;
